import { useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import { Loader, Scale, Timer } from 'lucide-react';
import { format } from 'date-fns';
import { SectionHeader } from '../ui/SectionHeader';
import type { Schedule } from '../../types/scheduledCommands';
import { CROWDED_GAP_SECONDS, gapStats, projectFires } from '../../utils/scheduledCommands';
import { formatInterval } from '../../utils/interval';
import { cn } from '../../utils/cn';

type Range = '1h' | '6h' | '24h';

const RANGES: { id: Range; seconds: number; label: string; step: number }[] = [
  { id: '1h', seconds: 3600, label: 'Next hour', step: 600 },
  { id: '6h', seconds: 21_600, label: '6 hours', step: 3600 },
  { id: '24h', seconds: 86_400, label: '24 hours', step: 4 * 3600 },
];

interface ScheduleTimelineProps {
  schedules: Schedule[];
  names: Map<number, string>;
  nowSec: number;
  /** A schedule to pick out (the table row under the pointer). */
  highlightId: number | null;
  onSelect: (id: number) => void;
  canRebalance: boolean;
  rebalancing: boolean;
  onRebalance: () => void;
  /** Holds the previous frame, dimmed, while the list reloads. */
  refreshing?: boolean;
}

const gapLabel = (seconds: number | null) => (seconds === null ? '—' : formatInterval(Math.max(1, Math.round(seconds))));

/**
 * When the upcoming runs fall, on one strip.
 *
 * Mr. Roboto spreads schedules out so two announcements never land in the same
 * breath; this is where staff can see that it worked. Each run is a tick, and
 * runs within 30 seconds of a neighbour are drawn in the warning colour and
 * counted in the key, so "crowded" never rests on colour alone. The schedule
 * table below is the same information as rows.
 */
export const ScheduleTimeline = ({
  schedules,
  names,
  nowSec,
  highlightId,
  onSelect,
  canRebalance,
  rebalancing,
  onRebalance,
  refreshing = false,
}: ScheduleTimelineProps) => {
  const [rangeId, setRangeId] = useState<Range>('1h');
  const [focus, setFocus] = useState<number | null>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const range = RANGES.find((r) => r.id === rangeId) ?? RANGES[0];
  const end = nowSec + range.seconds;

  const fires = useMemo(() => projectFires(schedules, nowSec, end, nowSec), [schedules, nowSec, end]);
  const stats = useMemo(() => gapStats(fires), [fires]);
  const position = (at: number) => ((at - nowSec) / range.seconds) * 100;

  // Clock labels on clean boundaries (every 10 minutes, hour, or 4 hours).
  const labels = useMemo(() => {
    const out: number[] = [];
    for (let t = Math.ceil((nowSec + range.step / 4) / range.step) * range.step; t < end - range.step / 4; t += range.step) out.push(t);
    return out;
  }, [nowSec, end, range.step]);

  const nearest = (clientX: number): number | null => {
    const rect = stripRef.current?.getBoundingClientRect();
    if (!rect || fires.length === 0) return null;
    const at = nowSec + ((clientX - rect.left) / rect.width) * range.seconds;
    let best = 0;
    for (let i = 1; i < fires.length; i += 1) {
      if (Math.abs(fires[i].at - at) < Math.abs(fires[best].at - at)) best = i;
    }
    // Only answer within 16px of a run, so empty stretches stay quiet.
    const pixels = (Math.abs(fires[best].at - at) / range.seconds) * rect.width;
    return pixels <= 16 ? best : null;
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => setFocus(nearest(e.clientX));

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (fires.length === 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const delta = e.key === 'ArrowRight' ? 1 : -1;
      setFocus((i) => (i === null ? (delta > 0 ? 0 : fires.length - 1) : Math.min(fires.length - 1, Math.max(0, i + delta))));
    } else if (e.key === 'Enter' && focus !== null) {
      onSelect(fires[focus].id);
    } else if (e.key === 'Escape') {
      setFocus(null);
    }
  };

  // Everything due at the focused moment (several schedules can share a second).
  const focused = focus !== null ? fires[focus] : null;
  const together = focused ? fires.filter((f) => Math.abs(f.at - focused.at) < CROWDED_GAP_SECONDS) : [];

  return (
    <div className="glass-card p-4 md:p-5" data-tour="schedules-timeline">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-3">
        <SectionHeader
          title="Upcoming runs"
          icon={Timer}
          description="Each tick is one run. Mr. Roboto spaces them out whenever a schedule is added, changed or resumed."
          className="mb-0"
        />
        <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="segmented" role="radiogroup" aria-label="Time range">
              {RANGES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  role="radio"
                  aria-checked={rangeId === r.id}
                  className="segmented-item segmented-item-sm"
                  data-active={rangeId === r.id}
                  onClick={() => {
                    setRangeId(r.id);
                    setFocus(null);
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {canRebalance && (
              <button
                type="button"
                onClick={onRebalance}
                disabled={rebalancing || schedules.length < 2}
                className="glass-button glass-button-sm"
                title="Spread every active schedule out again now. This moves when each one runs, but not how often."
              >
                {rebalancing ? <Loader className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
                <span className="hidden sm:inline">Rebalance</span>
              </button>
            )}
        </div>
      </div>

      <div className={cn('transition-opacity', refreshing && 'opacity-60')}>
        <div
          ref={stripRef}
          role="group"
          tabIndex={0}
          aria-label={`${fires.length} runs in the ${range.label.toLowerCase()}. Use the arrow keys to step through them.`}
          onPointerMove={onPointerMove}
          onPointerLeave={() => setFocus(null)}
          onClick={(e) => {
            // A tap has no pointer-move before it, so find the run from the tap itself.
            const index = focus ?? nearest(e.clientX);
            if (index !== null) onSelect(fires[index].id);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => setFocus(null)}
          className={cn('relative h-12 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-accent-400/60', fires.length > 0 && 'cursor-pointer')}
        >
          {/* Baseline and the "now" edge */}
          <div className="absolute inset-x-0 bottom-3 h-px bg-glass-200/15" />
          <div className="absolute left-0 top-1 bottom-3 w-px bg-glass-200/30" />

          {fires.map((fire, index) => {
            const crowded = stats.crowded.has(index);
            const picked = highlightId !== null && fire.id === highlightId;
            const dimmed = highlightId !== null && !picked;
            return (
              <span
                key={`${fire.id}-${fire.at}`}
                aria-hidden="true"
                className={cn(
                  'absolute bottom-3 w-0.5 -translate-x-1/2 rounded-t-sm transition-colors',
                  crowded ? 'h-7 bg-amber-400' : 'h-5 bg-accent-400',
                  picked && !crowded && 'bg-accent-300',
                  picked && 'h-8',
                  dimmed && (crowded ? 'bg-amber-400/35' : 'bg-glass-400/35'),
                  focus === index && 'ring-2 ring-white/60',
                )}
                style={{ left: `${position(fire.at)}%` }}
              />
            );
          })}

          {/* Clock labels */}
          {labels.map((t, i) => (
            <span
              key={t}
              aria-hidden="true"
              className={cn(
                'absolute bottom-0 -translate-x-1/2 translate-y-full pt-0.5 text-[10px] leading-none text-glass-500 tabular-nums',
                // Keep clear of "Now", and thin the labels out on a phone.
                position(t) < 7 && 'hidden',
                position(t) < 12 && 'max-sm:hidden',
                i % 2 === 1 && 'max-sm:hidden',
              )}
              style={{ left: `${position(t)}%` }}
            >
              {format(new Date(t * 1000), 'HH:mm')}
            </span>
          ))}
          <span aria-hidden="true" className="absolute bottom-0 left-0 translate-y-full pt-0.5 text-[10px] leading-none text-glass-400">Now</span>

          {/* Readout for the run under the pointer or keyboard focus */}
          {focused && (
            <div
              role="status"
              className="absolute bottom-full mb-2 -translate-x-1/2 z-10 min-w-[12rem] max-w-xs rounded-lg border border-glass-200/15 bg-[var(--bg-elevated)] px-3 py-2 shadow-popover pointer-events-none"
              style={{ left: `${Math.min(88, Math.max(12, position(focused.at)))}%` }}
            >
              <p className="text-sm font-semibold text-white tabular-nums">
                {format(new Date(focused.at * 1000), 'HH:mm:ss')}
                <span className="ml-1.5 text-xs font-normal text-glass-400">in {formatInterval(Math.max(1, focused.at - nowSec))}</span>
              </p>
              <ul className="mt-1 space-y-0.5">
                {together.map((f) => (
                  <li key={`${f.id}-${f.at}`} className="flex items-center gap-2 text-xs text-glass-300">
                    <span className={cn('inline-block w-2.5 h-0.5 shrink-0 rounded-full', together.length > 1 ? 'bg-amber-400' : 'bg-accent-400')} />
                    <span className="truncate">{names.get(f.id) ?? `Schedule ${f.id}`}</span>
                  </li>
                ))}
              </ul>
              {together.length > 1 && (
                <p className="mt-1 text-[11px] text-amber-300">Within {CROWDED_GAP_SECONDS} s of each other.</p>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-glass-400">
          <span>
            <span className="text-white tabular-nums">{fires.length}</span> run{fires.length === 1 ? '' : 's'}
          </span>
          <span>
            Closest gap <span className="text-white tabular-nums">{gapLabel(stats.closest)}</span>
          </span>
          <span>
            Average gap <span className="text-white tabular-nums">{gapLabel(stats.average)}</span>
          </span>
          {stats.crowded.size > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-0.5 h-3 rounded-sm bg-amber-400" aria-hidden="true" />
              <span>
                <span className="text-white tabular-nums">{stats.crowded.size}</span> within {CROWDED_GAP_SECONDS} s of another run
              </span>
            </span>
          )}
          {fires.length === 0 && <span>Nothing is due in this window.</span>}
        </div>
      </div>
    </div>
  );
};
